import { Fragment } from 'react';
import { t } from 'utils/getTranslations';
import { ReportEntry } from './reportCatalog';
import { ReportTags } from './ReportTags';
import { ReportToneLegend, ToneLegendEntry } from './ReportToneLegend';

type Block = { kind: 'heading' | 'paragraph'; text: string } | { kind: 'list'; items: string[] };

/**
 * Splits an About text into blocks: a line that starts with `## ` is a heading, consecutive lines that start
 * with `- ` are one bullet list, and every other run of lines is a paragraph. Blank lines separate blocks.
 */
export function parseAboutText(text: string): Block[] {
    const blocks: Block[] = [];
    for (const chunk of text.split(/\n\s*\n/)) {
        const lines = chunk
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
        let paragraph: string[] = [];
        let list: string[] = [];
        const flush = () => {
            if (paragraph.length > 0) blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
            if (list.length > 0) blocks.push({ kind: 'list', items: list });
            paragraph = [];
            list = [];
        };
        for (const line of lines) {
            if (line.startsWith('## ')) {
                flush();
                blocks.push({ kind: 'heading', text: line.slice(3) });
            } else if (line.startsWith('- ')) {
                if (paragraph.length > 0) flush();
                list.push(line.slice(2));
            } else {
                if (list.length > 0) flush();
                paragraph.push(line);
            }
        }
        flush();
    }
    return blocks;
}

type Props = {
    report: ReportEntry;
    baseCurrency: string | null;
    /** The figures the report colours; the panel ends with their colour key. */
    toneLegend?: ToneLegendEntry[];
};

/**
 * The About text of a report under its tags; the text names the firm's base currency, with its code when the code
 * is known.
 */
export function ReportAbout({ report, baseCurrency, toneLegend = [] }: Props) {
    const currencyName = baseCurrency
        ? t('reporting.base-currency-code', { currency: baseCurrency })
        : t('reporting.base-currency');
    const blocks = parseAboutText(t(`reporting.${report.key}.about`, { baseCurrency: currencyName }));
    return (
        <section
            aria-label={t('reporting.about-this-report')}
            className="bg-card ring-foreground/10 text-foreground flex max-w-4xl flex-col gap-2 rounded-xl p-5 text-sm ring-1"
        >
            <ReportTags report={report} />
            {blocks.map((block, index) => (
                <Fragment key={index}>
                    {block.kind === 'heading' && (
                        <h3 className="m-0 mt-2 text-sm font-semibold first:mt-0">{block.text}</h3>
                    )}
                    {block.kind === 'paragraph' && <p className="m-0">{block.text}</p>}
                    {block.kind === 'list' && (
                        <ul className="m-0 flex list-disc flex-col gap-1 pl-5">
                            {block.items.map((item, itemIndex) => (
                                <li key={itemIndex}>{item}</li>
                            ))}
                        </ul>
                    )}
                </Fragment>
            ))}
            <ReportToneLegend entries={toneLegend} />
        </section>
    );
}
